/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { cloneDeep } from 'lodash';
import { convertToDateFormat } from 'blb-utils/date';
import { DateTime } from 'luxon';
const notarizationInitialState = (closingType, options) => {
  if (isClosingRON(closingType)) {
    return closingType;
  }
  if (options !== undefined && options.length > 0) {
    return options[0].value;
  }
  return 'IN_OFFICE';
};
export let TaskDisplayStates = /*#__PURE__*/function (TaskDisplayStates) {
  TaskDisplayStates["CONFIRM"] = "CONFIRM";
  TaskDisplayStates["EDIT"] = "EDIT";
  return TaskDisplayStates;
}({});

/**
 * A flag to indicate if the supplied closing is a RON closing.
 * @param  {BlendBridge.API.ScheduleClosingAppointmentTaskClosingType} closingType Closing Type to assert
 * @return {Boolean}                                                               Flag of whether this closing type is a RON closing
 * @function
 */
export const isClosingRON = closingType => {
  return closingType === 'BLEND_SIGNING_ROOM' || closingType === 'NOTARYCAM';
};

/**
 * Empty Address typed object for use when toggling notarization method.
 * @type {BlendBridge.API.ScheduleTaskBorrowerPreferredAddress}
 */
export const emptyAddress = {
  addressLineOne: '',
  addressLineTwo: '',
  city: '',
  state: '',
  zipCode: ''
};

/**
 * Helper method to create the options to be displayed in the Notarization Platfom select input.
 * @param  {BlendBridge.API.ScheduleClosingAppointmentTaskClosingType} closingType Closing Type to use
 * @return {Dropdown.Option[]}                                                     Array of Dropdown.Option
 * @function
 */
export const notarizationPlatformOptions = closingType => {
  const optionsMap = {
    BLEND_SIGNING_ROOM: [{
      text: 'Blend Signing Room',
      value: 'BLEND_SIGNING_ROOM'
    }],
    NOTARYCAM: [{
      text: 'NotaryCam',
      value: 'NOTARYCAM'
    }]
  };
  return optionsMap[closingType] ?? [];
};

/**
 * Initialzes the Form Data based on supplied props.
 * This object gets sent as the payload to complete the Task.
 * @param  {ScheduleClosingAppointmentTask.Props}            props Props of Parent
 * @return {BlendBridge.API.ScheduleClosingTaskCompleteMeta}       Initial metadata to complete the task
 * @function
 */
export const initializeFormData = props => {
  const {
    borrowerPreferredAddress,
    borrowerPreferredMethod,
    borrowerPreferredTimezone,
    closingDate,
    closingType,
    notarizationMethodOptions
  } = props;
  const formData = {
    appointmentInformation: {
      date: closingDate ? convertToDateFormat(closingDate, 'MM/dd/yyyy') : '',
      notarization: notarizationInitialState(closingType, notarizationMethodOptions),
      time: '',
      timezone: borrowerPreferredTimezone
    },
    notaryInformation: {
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: ''
    }
  };
  if (!isClosingRON(closingType)) {
    formData.appointmentInformation.address = borrowerPreferredMethod === 'MOBILE' ? borrowerPreferredAddress ?? emptyAddress : emptyAddress;
  }
  return formData;
};

/**
 * Finds a Select Option from the supplied list of Dropdown.Options,
 * based on the supplied value. Returning `undefined` if unable to find.
 * @param  {Dropdown.Option[]}           options An array of Dropdown.Option objects
 * @param  {string}                      value   String Value to search for in Options
 * @return {Dropdown.Option | undefined}         Found Dropdown.Option or undefined in no match
 * @function
 */
export const findOptionByValue = (options, value) => options.find(option => option.value === value);

/**
 * Formats the Address type into a human readable string in the format:
 * addr.lineOne[, addr.lineTwo], addr.city, addr.state addr.zip
 * @param  {BlendBridge.API.ScheduleAddress} string Address to convert into String
 * @return {string}                                 Formatted string
 * @function
 */
export const formatAddressString = address => {
  const commaSeparatedStrings = [address.addressLineOne, address.addressLineTwo, address.city].filter(Boolean);
  const nonCommaSeparatedStrings = [address.state, address.zipCode].filter(Boolean);
  return [commaSeparatedStrings.join(', '), nonCommaSeparatedStrings.join(' ')].join(', ');
};

/**
 * Creates the payload to be sent to EClose based off the supplied form
 * data, in an attempt to keep the form data immutable - as formatting and optional
 * fields sent over to EClose may differ from the form data.
 * @param  {string}                                          closingDate ISO string of closing
 * @param  {BlendBridge.API.ScheduleClosingTaskCompleteMeta} formValues  Form values to be POSTed to EClose
 * @return {BlendBridge.API.ScheduleClosingTaskCompleteMeta}             Meta data formatted to align with EClose validation
 * @function
 */
export const createTaskCompletePayload = (formValues, closingDate) => {
  const payload = {
    appointmentInformation: cloneDeep(formValues.appointmentInformation),
    notaryInformation: formValues.notaryInformation
  };

  // convert date back to ISO
  // using original value supplied as current implementations
  // do not allow you to modify the date
  // or take in current input and convert to ISO with provided timezone

  if (!!closingDate) {
    payload.appointmentInformation.date = closingDate;
  } else {
    const newDate = DateTime.fromFormat(payload.appointmentInformation.date, 'MM/dd/yyyy', {
      zone: payload.appointmentInformation.timezone
    }).set({
      hour: 12
    }).toISO();
    payload.appointmentInformation.date = newDate;
  }

  // remove addressLineTwo if not defined
  if (payload.appointmentInformation.address && payload.appointmentInformation.address.addressLineTwo?.trim() === '') {
    delete payload.appointmentInformation.address.addressLineTwo;
  }
  return payload;
};